import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import './order-list.scss';
import { Typography } from '../../components/typography/typography';
import { PaginationData } from '../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../components/table/data-table';
import {
  Order,
  OrderDocumentType,
  OrderSearchRequest,
  OrderStatus,
  OrderType,
  OrderWorkFlowStatus,
} from '../../clients/quipli-service/order/order';
import { User, UserAddress } from '../../clients/quipli-service/user/user';
import {
  ActionCell,
  AmountCell,
  AssignedUnitCell,
  CustomerNameCell,
  CustomerPhoneNumberCell,
  DateTimeCell,
  DeliveryCell,
  OrderNumberCell,
  LocationCell,
  OrderStatusCell,
  PaymentMethodCell,
  RowNumCell,
  CreatedByCell,
} from './order-table-cells';
import { Location } from '../../clients/quipli-service/location/location';
import { SearchBar } from '../../components/search-bar/search-bar';
import { SearchDropdownOption } from '../../components/dropdown/search-dropdown';
import { DateTime, Interval } from 'luxon';
import Icon from '../../components/icons/icon';
import { Dropdown } from '../../components/dropdown/dropdown';
import { Button } from '../../components/form/button';
import { FiltersModal } from '../../components/modal/filters-modal';
import { DatePicker } from '../../components/date-picker/date-picker';
import { SelectForm } from '../../components/select-form/select-form';
import { QuipliLink } from '../../components/link/quipli-link';
import { QrScanner } from '@yudiel/react-qr-scanner';
import { Modal } from '../../components/modal/modal';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { useAdmin } from '../../hooks/use-admin-context';
import { isMobileOnly } from 'react-device-detect';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { useSearchParams } from 'react-router-dom';
import { UserSearch } from '../../components/user-search/user-search';
import { Badge } from '../../components/badge/badge';
import { ToggleTabNav } from '../../components/toggle-tab-nav/toggle-tab-nav';
import { toast } from '../../util/toasts';
import { useOrderApiV1 } from '../../clients/quipli-service/order/order-api-v1';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { MissedOrder, RejectOrder } from './status-transitions/reject-order';
import { uniqBy } from 'lodash';
import { formatShippingType, ShippingType } from '../../clients/quipli-service/delivery/delivery';
import { useAdminApiV1 } from '../../clients/quipli-service/admin/admin-api-v1';
import { Admin } from '../../clients/quipli-service/admin/admin';
import FileSaver from 'file-saver';
import { UserAddressSearch } from '../../components/user-search/user-address-search';

type OrderListColumn =
  | 'OrderNumber'
  | 'CustomerName'
  | 'CustomerPhone'
  | 'RentalStart'
  | 'RentalEnd'
  | 'Location'
  | 'Delivery'
  | 'PaymentMethod'
  | 'AssignedUnit'
  | 'Amount'
  | 'CreatedBy'
  | 'Status'
  | 'ActionColumn'
  | 'RowNum'
  | 'ActionsButtons'
  | 'ActionsMobile'
  | 'CheckBoxColumn';

function extractOrderSearchParams(params: URLSearchParams): OrderSearchRequest {
  return {
    keyword: params.has('keyword') ? params.get('keyword')! : undefined,
    from: params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined,
    to: params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined,
    startDateFrom: params.has('startDateFrom') ? DateTime.fromISO(params.get('startDateFrom')!) : undefined,
    startDateTo: params.has('startDateTo') ? DateTime.fromISO(params.get('startDateTo')!) : undefined,
    endDateFrom: params.has('endDateFrom') ? DateTime.fromISO(params.get('endDateFrom')!) : undefined,
    endDateTo: params.has('endDateTo') ? DateTime.fromISO(params.get('endDateTo')!) : undefined,
    locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
    customerId: params.has('customerId') ? parseInt(params.get('customerId')!) : undefined,
    addressId: params.has('addressId') ? parseInt(params.get('addressId')!) : undefined,
    type: params.has('type') ? (params.get('type') as OrderType) : undefined,
    shippingType: params.has('shippingType') ? [params.get('shippingType') as ShippingType] : undefined,
    workFlowStatus: params.has('workFlowStatus')
      ? (params.get('workFlowStatus')!.split(',') as OrderWorkFlowStatus[])
      : undefined,
    createdBy: params.has('createdBy') ? parseInt(params.get('createdBy')!) : undefined,
    pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
    pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    sortBy: params.has('sortBy') ? (params.get('sortBy')! as OrderSearchRequest['sortBy']) : undefined,
    sortDirection: params.has('sortDirection') ? (params.get('sortDirection')! as 'desc' | 'asc') : undefined,
    documentType: params.has('documentType') ? (params.get('documentType') as OrderDocumentType) : undefined,
  };
}

export const OrderList = ({
  title = 'Manage Orders',
  createOrderButton = true,
  scanQrButton = true,
  allowWorkFlowStatusFilter = true,
  allowedColumns = [
    'OrderNumber',
    'CustomerName',
    'CustomerPhone',
    'RentalStart',
    'RentalEnd',
    'Location',
    'Delivery',
    'PaymentMethod',
    'AssignedUnit',
    'Amount',
    'CreatedBy',
    'Status',
    'ActionColumn',
    'RowNum',
    'ActionsButtons',
    'ActionsMobile',
    'CheckBoxColumn',
  ],
  defaultWorkFlowStatus,
  additionalAction,
  user,
  documentType = OrderDocumentType.Order,
}: {
  title?: string;
  createOrderButton?: boolean;
  scanQrButton?: boolean;
  allowWorkFlowStatusFilter?: boolean;
  allowedColumns?: OrderListColumn[];
  defaultWorkFlowStatus?: OrderWorkFlowStatus;
  additionalAction?: ReactNode;
  user?: User;
  documentType?: OrderDocumentType;
}) => {
  const { searchOrders, listLocations, getMultipleConfigurations, getUserById, getUserAddress } = useAPI();
  const { exportOrders, generateInvoices } = useOrderApiV1();
  const { getAdminsList } = useAdminApiV1();

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();
  const [locations, setLocations] = useState<Location[]>([]);

  const [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false);
  const [scanQrModalOpen, setScanQrModalOpen] = useState<boolean>(false);
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);

  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const abortRef = useRef<AbortController | null>(null);

  const getConfiguration = useCallback(
    (variable: string) => configurations.find((config) => config.variable === variable),
    [configurations],
  );
  const searchRef = useRef<any>(null);

  const doSearchOrders = useCallback(
    (_request: OrderSearchRequest) => {
      if (configurations.length && locations.length) {
        setIsTableDataLoading(true);

        if (abortRef.current) {
          abortRef.current.abort();
        }

        const { signal } = (abortRef.current = new AbortController());

        searchOrders({
          ..._request,
          documentType,
          customerId: user?.id,
          workFlowStatus: defaultWorkFlowStatus ? [defaultWorkFlowStatus] : _request.workFlowStatus,
        })
          .then(({ items, pagination }) => {
            setTableData(
              items.map((item) =>
                buildDataRow(
                  item,
                  locations,
                  getConfiguration('CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
                  getConfiguration('CONF_DELETE_ORDERS')?.value === '1',
                ),
              ),
            );
            setTablePagination(pagination);
            setIsTableDataLoading(false);
          })
          .catch((e) => {
            if (signal.aborted) {
              return;
            }

            toast(e.message, 'error');
            setIsTableDataLoading(false);
            setTableData([]);
          });
      }
    },
    [configurations, locations],
  );

  // List request hooks
  const [params] = useSearchParams();
  const {
    request,
    setRequest,
    numOfParams: activeFilters,
  } = useRequestQueryParams<OrderSearchRequest>(extractOrderSearchParams(params), doSearchOrders, [
    configurations,
    locations,
  ]);

  const [rentalStartStartDate, setRentalStartStartDate] = useState<DateTime | undefined>();
  const [rentalStartEndDate, setRentalStartEndDate] = useState<DateTime | undefined>();
  const [rentalEndStartDate, setRentalEndStartDate] = useState<DateTime | undefined>();
  const [rentalEndEndDate, setRentalEndEndDate] = useState<DateTime | undefined>();
  const [startDate, setStartDate] = useState<DateTime | undefined>();
  const [endDate, setEndDate] = useState<DateTime | undefined>();
  const [selectedUser, setSelectedUser] = useState<User>();
  const [selectedAddress, setSelectedAddress] = useState<UserAddress>();
  const [selectedOrderType, setSelectedOrderType] = useState<OrderType>();
  const [selectedLocation, setSelectedLocation] = useState<number>();
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<ShippingType>();
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [modal, setModalAction] = useState<ReactNode | undefined>();

  const clearAll = () => {
    setRentalStartStartDate(undefined);
    setRentalStartEndDate(undefined);
    setRentalEndStartDate(undefined);
    setRentalEndEndDate(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedUser(undefined);
    setSelectedAddress(undefined);
    setSelectedOrderType(undefined);
    setSelectedLocation(undefined);
    setSelectedDeliveryType(undefined);
    setSelectedAdmin(null);

    if (searchRef.current?.setKeyword) {
      searchRef.current.setKeyword('');
    }

    setRequest((prevState) => ({
      pageNumber: 1,
      pageSize: prevState.pageSize,
      keyword: undefined,
      status: undefined,
      returnStatus: undefined,
      from: undefined,
      to: undefined,
      startDateFrom: undefined,
      startDateTo: undefined,
      endDateFrom: undefined,
      endDateTo: undefined,
      customerId: undefined,
      type: undefined,
      shippingType: undefined,
      locationId: undefined,
    }));
  };

  const columns: DataTableColumn[] = [
    { key: 'CheckBoxColumn', isCheckBoxColumn: true },
    { key: 'OrderNumber', label: 'Order No.', showRule: 'OnlyDesktop' },
    { key: 'CustomerName', label: 'Customer', showRule: 'OnlyDesktop' },
    { key: 'CustomerPhone', label: 'Tel. No.', showRule: 'Both' },
    { key: 'RentalStart', label: 'Rental Start', showRule: 'Both', sortable: true, sortField: 'rentalStart' },
    { key: 'RentalEnd', label: 'Rental End', showRule: 'Both', sortable: true, sortField: 'rentalEnd' },
    { key: 'Location', label: 'Location', showRule: 'Both', hide: locations.length === 1 },
    { key: 'Delivery', label: 'Delivery', showRule: 'Both' },
    { key: 'PaymentMethod', label: 'Payment', showRule: 'Both' },
    { key: 'AssignedUnit', label: 'Name / Unit', showRule: 'Both' },
    { key: 'Amount', label: 'Total', showRule: 'Both' },
    { key: 'CreatedBy', label: 'Created By', showRule: 'Both' },
    { key: 'Status', label: 'Status', showRule: 'Both' },
    { key: 'ActionColumn', label: 'Actions', showRule: 'Both' },
    { key: 'RowNum', label: '', showRule: 'OnlyDesktop' },
    { key: 'ActionsButtons', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
    { key: 'ActionsMobile', label: '', fixedAsLast: true, showRule: 'OnlyMobile' },
  ];

  const buildDataRow = (
    order: Order,
    locations: Location[],
    trackingVia: 'serialNumber' | 'unitNumber',
    canDeleteOrders = false,
  ) => {
    const rentals = order.products.filter((op) => op.type === 'Rental');
    const startDate =
      rentals.length > 0 && DateTime.min(...rentals.map((op) => op.actualRentalStartDate || op.rentalStartDate));
    const endDate =
      rentals.some((op) => op.rentalEndDate) &&
      DateTime.max(...rentals.flatMap((op) => (op.rentalEndDate ? [op.actualRentalEndDate || op.rentalEndDate] : [])));

    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {order.user.name}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Order NO{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${order.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {order.orderNumber || 'N/A'}
            </QuipliLink>
          </Typography>
        </>
      ),
      fields: {
        OrderNumber: <OrderNumberCell order={order} />,
        CustomerName: <CustomerNameCell order={order} />,
        CustomerPhone: <CustomerPhoneNumberCell order={order} />,
        RentalStart: startDate && <DateTimeCell date={startDate} />,
        RentalEnd: endDate && <DateTimeCell date={endDate} />,
        Location: <LocationCell order={order} locations={locations} />,
        Delivery: <DeliveryCell order={order} />,
        PaymentMethod: <PaymentMethodCell order={order} />,
        AssignedUnit: (
          <AssignedUnitCell order={order} trackingVia={trackingVia} locations={locations} isOrderList={true} />
        ),
        Amount: <AmountCell order={order} locations={locations} />,
        CreatedBy: <CreatedByCell admin={order.admin} />,
        Status: <OrderStatusCell order={order} />,
        ActionColumn: <ActionCell order={order} locations={locations} reload={() => doSearchOrders(request)} />, //pop
        RowNum: <RowNumCell order={order} />,
        ActionsButtons: locations.length && (
          <ActionsColumn
            order={order}
            location={locations.find((loc) => loc.id === order.locationId)!}
            canDeleteOrders={canDeleteOrders}
            setModalAction={setModalAction}
            onConfirm={async () => doSearchOrders(request)}
          />
        ),
        ActionsMobile: (
          <Button
            className="button-secondary w-full"
            onClick={() => {
              locations.length &&
                setModalAction(
                  <RejectOrder
                    order={order}
                    location={locations.find((loc) => loc.id === order.locationId)!}
                    onConfirm={() => {
                      doSearchOrders(request);
                      setModalAction(undefined);
                    }}
                    onClose={() => setModalAction(undefined)}
                  />,
                );
            }}
          >
            <Typography type="text-md" weight="medium">
              Cancel Order
            </Typography>
          </Button>
        ),
      },
      data: order,
    };
  };

  const locationOptions: SearchDropdownOption[] = locations.map(
    (loc) =>
      ({
        label: loc.name,
        value: loc.id + '',
      } as SearchDropdownOption),
  );

  const orderTypeOptions: SearchDropdownOption[] = [
    {
      value: OrderType.Sale,
      label: 'Sell',
    },
    {
      value: OrderType.Rental,
      label: 'Rent',
    },
  ];

  const deliveryTypeOptions = uniqBy(
    Object.values(ShippingType).map((value) => ({ value, label: formatShippingType(value) })),
    (option) => option.label,
  );

  useEffect(() => {
    async function init() {
      try {
        const locationItems = await listLocations();
        setLocations(locationItems);
      } catch (e: any) {
        setLocations([]);
      }

      const _admins = await getAdminsList();
      setAdmins(_admins);

      try {
        const response = await getMultipleConfigurations([
          'CONF_ENABLE_QR_CODE',
          'CONF_UNIT_TRACKING_VIA',
          'CONF_DELETE_ORDERS',
          'CONF_ALLOW_MULTI_LOCATION_UNITS',
          'CONF_TIMEZONE',
        ]);
        setConfigurations(response);
      } catch (e: any) {
        setConfigurations([]);
      }

      setRentalStartStartDate(params.has('startDateFrom') ? DateTime.fromISO(params.get('startDateFrom')!) : undefined);
      setRentalStartEndDate(params.has('startDateTo') ? DateTime.fromISO(params.get('startDateTo')!) : undefined);
      setRentalEndStartDate(params.has('endDateFrom') ? DateTime.fromISO(params.get('endDateFrom')!) : undefined);
      setRentalEndEndDate(params.has('endDateTo') ? DateTime.fromISO(params.get('endDateTo')!) : undefined);
      setStartDate(params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined);
      setEndDate(params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined);
      setSelectedOrderType(params.has('type') ? (params.get('type') as OrderType) : undefined);
      setSelectedDeliveryType(params.has('shippingType') ? (params.get('shippingType') as ShippingType) : undefined);
      setSelectedAdmin(
        params.has('createdBy')
          ? _admins.find((admin) => admin.adminId === parseInt(params.get('createdBy')!)) ?? null
          : null,
      );

      if (params.has('customerId')) {
        const _customerId = parseInt(params.get('customerId')!);
        getUserById(_customerId).then(setSelectedUser);
        if (params.has('addressId')) {
          getUserAddress(_customerId, parseInt(params.get('addressId')!)).then(setSelectedAddress);
        }
      }
    }
    init();
  }, []);

  const exportFile = async (orderIds: number[]) => {
    const file = await exportOrders(orderIds);
    const exportFileName = `Orders_Report_${DateTime.local().toFormat('yyyy-LL-dd')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  const downloadInvoicesPDF = async (orderIds: number[]) => {
    try {
      const file = await generateInvoices(orderIds);
      const fileType = 'application/pdf';

      const data = new Blob([file], { type: fileType });

      FileSaver.saveAs(data, `Invoices.pdf`);
    } catch (error: any) {
      toast(error.message || 'Failed to generate invoices', 'error');
    }
  };

  return (
    <>
      <div className="order-list container-v2 pb-5">
        <div className="flex justify-between items-center">
          <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
            {title}
          </Typography>
        </div>

        <div className="pt-2 flex flex-wrap justify-between gap-2 items-start">
          <div className="order-2 md:order-1 flex gap-2">
            <div className="flex gap-2 flex-wrap">
              <SearchBar
                ref={searchRef}
                data-cy="search-order"
                placeholder="Search by keyword"
                subtitle="Product, Customer, Email, Invoice/Reference Number, Notes, Inventory Tracking Number"
                defaultValue={request.keyword}
                setRequest={setRequest}
              />
              <Button
                className="button-secondary !border-0 h-[46px] relative"
                onClick={() => setFiltersModalOpen(true)}
                data-cy="filter-order"
              >
                <Typography
                  type="text-md"
                  className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                >
                  <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
                </Typography>

                {activeFilters > 0 && (
                  <Badge className="absolute -right-2 -top-2" text={`${activeFilters}`} size="small" type="yellow" />
                )}
              </Button>

              {activeFilters > 0 && (
                <Button
                  className="button-secondary !border-0 h-[46px]"
                  onClick={() => clearAll()}
                  data-cy="clear-filter-order"
                >
                  <Typography
                    type="text-md"
                    className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                  >
                    Clear Filters
                  </Typography>
                </Button>
              )}
            </div>
          </div>
          <div className="order-1 md:order2 flex gap-2">
            {scanQrButton && getConfiguration('CONF_ENABLE_QR_CODE')?.value === '1' && (
              <Button
                className="button-secondary h-[46px]"
                onClick={() =>
                  request.id
                    ? setRequest({
                        sortBy: 'id',
                        sortDirection: 'desc',
                      })
                    : setScanQrModalOpen(true)
                }
                data-cy="scan-order"
              >
                <Typography
                  type="text-md"
                  weight="semi-bold"
                  className="flex justify-center items-center gap-1 text-[var(--grey-700)]"
                >
                  Scan QR {request.id && <Icon icon="X" size="small" />}
                </Typography>
              </Button>
            )}

            {!isMobileOnly && (createOrderButton || additionalAction) && (
              <>
                {createOrderButton && (
                  <Button className="button-yellow !border-0 h-[46px]" data-cy="create-order">
                    <QuipliLink to="orders/create">
                      <Typography
                        type="text-md"
                        weight="semi-bold"
                        className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                      >
                        Create Order
                      </Typography>
                    </QuipliLink>
                  </Button>
                )}
                {additionalAction}
              </>
            )}
          </div>
        </div>

        <Modal title="Scan Order QR" isOpen={scanQrModalOpen} close={() => setScanQrModalOpen(false)}>
          <div className="bg-white p-5 md:w-[500px]">
            <QrScanner
              onDecode={(result) => {
                setScanQrModalOpen(false);
                if (result.includes('manager/orders/view/')) {
                  const orderId = result.substring(result.lastIndexOf('/') + 1);
                  setRequest({ id: Number(orderId) });
                }
              }}
              onError={() => {}}
            />
          </div>
        </Modal>

        <FiltersModal
          isOpen={filtersModalOpen}
          title="Filters"
          onConfirm={() => {
            setFiltersModalOpen(false);
            setIsTableDataLoading(true);
            setRequest((prevState) => ({
              ...prevState,
              ...{
                from: startDate,
                to: endDate,
                startDateFrom: rentalStartStartDate,
                startDateTo: rentalStartEndDate,
                endDateFrom: rentalEndStartDate,
                endDateTo: rentalEndEndDate,
                customerId: selectedUser?.id,
                addressId: selectedAddress?.id,
                type: selectedOrderType,
                shippingType: selectedDeliveryType !== undefined ? [selectedDeliveryType] : undefined,
                locationId: selectedLocation,
                createdBy: selectedAdmin?.adminId,
              },
            }));
          }}
          onCancel={() => {
            setFiltersModalOpen(false);
          }}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <Typography type="text-md" weight="medium">
                Rental Start Range
              </Typography>
              <div className="flex flex-wrap gap-2">
                <DatePicker
                  value={rentalStartStartDate}
                  onChange={(d) =>
                    setRentalStartStartDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).startOf('day'),
                    )
                  }
                />
                <DatePicker
                  value={rentalStartEndDate}
                  onChange={(d) =>
                    setRentalStartEndDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).endOf('day'),
                    )
                  }
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Typography type="text-md" weight="medium">
                Rental End Range
              </Typography>
              <div className="flex flex-wrap gap-2">
                <DatePicker
                  value={rentalEndStartDate}
                  onChange={(d) =>
                    setRentalEndStartDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).startOf('day'),
                    )
                  }
                />
                <DatePicker
                  value={rentalEndEndDate}
                  onChange={(d) =>
                    setRentalEndEndDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).endOf('day'),
                    )
                  }
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Typography type="text-md" weight="medium">
                Rental Range
              </Typography>
              <div className="flex flex-wrap gap-2">
                <DatePicker
                  value={startDate}
                  onChange={(d) =>
                    setStartDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).startOf('day'),
                    )
                  }
                />
                <DatePicker
                  value={endDate}
                  onChange={(d) =>
                    setEndDate(
                      d?.setZone(getConfiguration('CONF_TIMEZONE')?.value, { keepLocalTime: true }).endOf('day'),
                    )
                  }
                  disabledIntervals={[Interval.before(startDate || DateTime.now(), { years: 10 })]}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Typography type="text-md" weight="medium">
                Location
              </Typography>
              <div className="flex gap-2 w-full">
                <SelectForm
                  className="w-full break-all"
                  defaultValue={
                    selectedLocation
                      ? ({
                          value: `${selectedLocation}` || undefined,
                          label: locationOptions.find((loc) => loc.value === `${selectedLocation}`)?.label,
                        } as SearchDropdownOption)
                      : undefined
                  }
                  options={locationOptions}
                  onChange={(ps) => setSelectedLocation(ps?.value as any)}
                  isClearable={true}
                  menuPlacement="bottom"
                  placeholder="Select"
                />
              </div>
            </div>

            {!user && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Customer
                </Typography>
                <div className="flex gap-2 w-full">
                  <UserSearch locations={locations} selectedUser={selectedUser} onSelectUser={setSelectedUser} />
                </div>
              </div>
            )}

            {!!selectedUser && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Address
                </Typography>
                <UserAddressSearch
                  userId={selectedUser.id}
                  selectedAddress={selectedAddress}
                  onSelectAddress={setSelectedAddress}
                />
              </div>
            )}

            <div className="flex flex-col gap-2 w-full">
              <Typography type="text-md" weight="medium">
                Order Type
              </Typography>
              <div className="flex gap-2 w-full">
                <SelectForm
                  className="w-full break-all"
                  defaultValue={orderTypeOptions.filter((elem) => elem.value === selectedOrderType)}
                  options={orderTypeOptions}
                  onChange={(ps) => setSelectedOrderType(ps?.value as any)}
                  isClearable={true}
                  isSearchable={false}
                  menuPlacement="bottom"
                  placeholder="Select"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Typography type="text-md" weight="medium">
                Delivery Type
              </Typography>
              <div className="flex gap-2 w-full">
                <SelectForm
                  className="w-full break-all"
                  defaultValue={deliveryTypeOptions.filter((elem) => elem.value === selectedDeliveryType)}
                  options={deliveryTypeOptions}
                  onChange={(ps) => setSelectedDeliveryType(ps?.value as any)}
                  isClearable={true}
                  isSearchable={false}
                  menuPlacement="auto"
                  placeholder="Select"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Typography type="text-md" weight="medium">
                Created By
              </Typography>
              <div className="flex gap-2 w-full">
                <SelectForm
                  className="w-full break-all"
                  defaultValue={admins.filter((elem) => elem.adminId === selectedAdmin?.adminId)}
                  options={admins}
                  getOptionLabel={(option) => option.adminFullName}
                  getOptionValue={(option) => `${option.adminId}`}
                  onChange={(op) => setSelectedAdmin(op)}
                  isClearable={true}
                  isSearchable={false}
                  menuPlacement="auto"
                  placeholder="Select"
                />
              </div>
            </div>
          </div>
        </FiltersModal>

        {allowWorkFlowStatusFilter && (
          <div className="flex w-full items-center mt-4 justify-start gap-2 overflow-x-auto">
            <ToggleTabNav
              onSelect={({ workFlowStatus }) => {
                setRequest((prevState) => ({
                  ...prevState,
                  workFlowStatus: workFlowStatus?.length ? workFlowStatus : undefined,
                }));
              }}
              selectedTabs={request.workFlowStatus || []}
              className="bg-transparent !mt-0"
            />
          </div>
        )}

        <DataTable
          wrapperClassNames="mt-4"
          isLoading={isTableDataLoading}
          columnNames={columns.filter((col) => allowedColumns.includes(col.key as OrderListColumn))}
          data={tableData}
          pagination={tablePagination}
          request={request}
          setRequest={setRequest}
          onCheckBoxSelect={(items) => setSelectedOrders(items)}
          checkBoxActions={
            <div className="flex gap-3">
              <div
                className="cursor-pointer"
                onClick={() => downloadInvoicesPDF(selectedOrders.map((order) => order.id))}
              >
                <Typography className="flex justify-center justify-items-center gap-1 text-[white]" type="text-md">
                  <Icon icon="Download" stroke="white" /> Download as PDF
                </Typography>
              </div>
              <Icon icon="Separator" />
              <div className="cursor-pointer" onClick={() => exportFile(selectedOrders.map((order) => order.id))}>
                <Typography className="flex justify-center justify-items-center gap-1 text-[white]" type="text-md">
                  <Icon icon="Download" stroke="white" /> Download as XLS
                </Typography>
              </div>
            </div>
          }
          sort={{
            field: request.sortBy || 'id',
            direction: request.sortDirection || 'desc',
            onSort: (sortBy, sortDirection) => {
              setRequest((prevState) => ({
                ...prevState,
                sortBy: sortBy as OrderSearchRequest['sortBy'],
                sortDirection,
              }));
            },
          }}
          cypressPrevious="previous_order_page"
          cypressNext="next_order_page"
        />
      </div>

      {isMobileOnly && (
        <QuipliLink to="orders/create">
          <div className="block fixed left-0 right-0 bottom-2 w-full p-2 mr-[-10px]">
            <Button className="button-yellow w-full" data-cy="add-location">
              <Typography
                type="text-md"
                weight="semi-bold"
                className="flex justify-center justify-items-center gap-1 text-grey-700"
              >
                <Icon icon="Plus" stroke="var(--grey-700)" />
                Create Order
              </Typography>
            </Button>
          </div>
        </QuipliLink>
      )}

      {modal}
    </>
  );
};

const ActionsColumn = ({
  order,
  location,
  canDeleteOrders,
  setModalAction,
  onConfirm,
}: {
  order: Order;
  location: Location;
  canDeleteOrders: boolean;
  setModalAction: (modal: ReactNode) => void;
  onConfirm: () => Promise<void>;
}) => {
  const { contextPath, isSuperAdmin } = useAdmin();
  const { deleteOrder } = useOrderApiV1();
  return (
    <Dropdown
      placement="auto-end"
      closeOnClickInside={false}
      trigger={() => (
        <Button className="button-tertiary" data-cy={`customer_menu`}>
          <Icon icon="DotsVertical" />
        </Button>
      )}
    >
      <div
        className="min-w-full flex justify-start items-center gap-2 px-0 cursor-pointer"
        onClick={() => {
          window.open(`${contextPath}quipli-service/yorent/v2/orders/${order.id}/invoices/${order.invoices[0].id}`);
        }}
      >
        <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
          <Icon icon="Download" stroke="black" /> Download Invoice
        </Typography>
      </div>
      {![OrderStatus.Cancelled, OrderStatus.Missed].includes(order.status) && (
        <div
          className="min-w-full flex justify-start items-center gap-2 px-0 cursor-pointer"
          onClick={() => {
            setModalAction(
              <RejectOrder
                order={order}
                location={location}
                onConfirm={onConfirm}
                onClose={() => setModalAction(undefined)}
              />,
            );
          }}
        >
          <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
            <Icon icon="XCircle" stroke="black" /> Cancel Order
          </Typography>
        </div>
      )}
      {order.status === OrderStatus.Requested && (
        <div
          className="min-w-full flex justify-start items-center gap-2 px-0 cursor-pointer"
          onClick={() => {
            setModalAction(
              <MissedOrder
                order={order}
                location={location}
                onConfirm={onConfirm}
                onClose={() => setModalAction(undefined)}
              />,
            );
          }}
        >
          <Typography className="flex justify-center justify-items-center gap-1" type="text-sm">
            <Icon icon="InfoCircle" stroke="black" /> Mark As Missed Order
          </Typography>
        </div>
      )}
      {(canDeleteOrders || isSuperAdmin) && (
        <div
          className="min-w-full flex justify-start items-center gap-2 px-0 cursor-pointer"
          onClick={() => {
            setModalAction(
              <ConfirmModal
                isOpen={true}
                close={() => {
                  setModalAction(undefined);
                }}
                onConfirm={async () => {
                  try {
                    await deleteOrder(order.id);
                    toast('Order successfully deleted');
                    onConfirm();
                    setModalAction(undefined);
                  } catch {
                    toast('Error deleting order', 'error');
                  }
                }}
                prompt={'Are you sure you want to delete this order?'}
                additionalText="This action cannot be undone!"
              />,
            );
          }}
        >
          <Typography className="flex justify-center justify-items-center gap-1 text-[red]" type="text-sm">
            <Icon icon="Trash" stroke="red" /> Delete Order
          </Typography>
        </div>
      )}
    </Dropdown>
  );
};
